import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, ArrowUpRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { brandPage, brand } from "../mock";

export default function Brand() {
  useReveal();
  const p = brandPage;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Brand Strategy & Design for Manufacturers | Nitin Gupta";
    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", content);
      return { tag, prev };
    };
    const d = setMeta(
      "description",
      "Nitin Gupta helps manufacturers build stronger brands through positioning, branding, exhibitions, digital experiences and buyer confidence."
    );
    return () => {
      document.title = prevTitle;
      if (d.prev != null) d.tag.setAttribute("content", d.prev);
    };
  }, []);

  const waHref = `https://wa.me/${brand.phoneRaw}?text=${encodeURIComponent(
    "Hello Nitin Ji, I want to talk about my brand."
  )}`;

  return (
    <main className="pt-24">
      {/* ===== HERO ===== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 lg:pt-14 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <p className="eyebrow">{p.hero.eyebrow}</p>
            <h1 className="mt-6 font-serif-display font-bold leading-[0.98] tracking-[-0.03em] text-5xl sm:text-6xl lg:text-7xl">
              Make your brand <span className="text-gradient-gold">worth more.</span>
            </h1>
            <p className="mt-7 text-xl sm:text-2xl font-light text-[var(--ivory)]/85 max-w-lg leading-snug">
              {p.hero.sub}
            </p>
            <p className="mt-5 text-[var(--muted)] leading-relaxed max-w-lg">{p.hero.body}</p>

            <div className="mt-8">
              <p className="font-serif-display text-lg font-semibold">{p.hero.goalTitle}</p>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[var(--ivory)]/80">
                {p.hero.goals.map((g) => (
                  <span key={g} className="font-serif-display text-lg">{g}</span>
                ))}
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-gold px-7 py-3.5 rounded-full inline-flex items-center gap-2 group">
                {p.hero.ctaPrimary}
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#work" className="btn-ghost px-7 py-3.5 rounded-full inline-flex items-center gap-2">
                {p.hero.ctaSecondary} <ArrowDown size={16} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <img
              src={p.hero.image}
              alt="Param Dairy exhibition booth"
              className="w-full rounded-2xl border border-[var(--line)] shadow-[0_30px_60px_-30px_rgba(16,16,20,0.35)]"
            />
          </div>
        </div>
      </section>

      {/* ===== REAL PROBLEM ===== */}
      <section className="bg-[var(--ink-2)] border-y border-[var(--line)] py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center reveal">
          <p className="eyebrow">{p.problem.eyebrow}</p>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-6xl font-bold leading-[1.04]">
            Your product is not the only thing buyers see.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {p.problem.touchpoints.map((t) => (
              <span key={t} className="px-5 py-2 rounded-full border border-[var(--line)] text-sm text-[var(--ivory)]/75">
                {t}
              </span>
            ))}
          </div>
          <p className="mt-10 text-lg font-light leading-relaxed text-[var(--muted)] max-w-2xl mx-auto">{p.problem.body}</p>
          <p className="mt-12 font-serif-display text-3xl sm:text-4xl font-bold text-gradient-gold">
            {p.problem.statement}
          </p>
        </div>
      </section>

      {/* ===== VALUE ===== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 reveal">
          <p className="eyebrow">{p.value.eyebrow}</p>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-6xl font-bold leading-[1.02]">
            <span className="block text-[var(--ivory)]/45">{p.value.title[0]}</span>
            <span className="block">{p.value.title[1]}</span>
          </h2>
          <p className="mt-8 text-lg text-[var(--muted)] leading-relaxed max-w-2xl">{p.value.body}</p>
          <p className="mt-8 font-serif-display text-2xl sm:text-3xl font-semibold max-w-2xl leading-snug">
            {p.value.question}
          </p>
          <p className="mt-6 text-[var(--muted)] leading-relaxed max-w-2xl">{p.value.close}</p>
        </div>
      </section>

      {/* ===== PARAM DAIRY CASE STUDY ===== */}
      <section id="work" className="bg-[var(--ink-2)] border-y border-[var(--line)] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <p className="eyebrow">{p.caseStudy.eyebrow}</p>
            <h2 className="mt-5 font-serif-display text-5xl sm:text-7xl font-bold tracking-[-0.03em]">
              {p.caseStudy.title}
            </h2>
            <p className="mt-4 font-serif-display text-xl sm:text-2xl text-[var(--gold)]">{p.caseStudy.subtitle}</p>
            <p className="mt-6 text-[var(--muted)] leading-relaxed">{p.caseStudy.body}</p>
            <p className="mt-8 font-serif-display text-2xl font-bold">{p.caseStudy.oneStrategy}</p>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[200px]">
            {p.caseStudy.gallery.map((g, i) => (
              <figure
                key={i}
                className={`reveal relative overflow-hidden rounded-xl border border-[var(--line)] bg-white group ${
                  g.span === "wide" ? "col-span-2" : ""
                } ${g.span === "tall" ? "row-span-2" : ""}`}
                style={{ transitionDelay: `${(i % 4) * 70}ms` }}
              >
                <img
                  src={g.src}
                  alt={g.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 text-xs font-semibold uppercase tracking-widest text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  {g.label}
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-16 max-w-3xl mx-auto text-center reveal">
            <div className="gold-line mb-10 opacity-50" />
            <blockquote className="font-serif-display text-3xl sm:text-4xl font-semibold leading-snug">
              “{p.caseStudy.testimonial.quote}”
            </blockquote>
            <p className="mt-6 font-semibold">{p.caseStudy.testimonial.author}</p>
            <p className="text-sm text-[var(--gold)]">{p.caseStudy.testimonial.role}</p>
          </div>
        </div>
      </section>

      {/* ===== FLOW ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 reveal">
          <p className="eyebrow text-center">{p.flow.eyebrow}</p>
          <div className="mt-10 flex flex-col items-center gap-3">
            {p.flow.steps.map((s, i) => (
              <React.Fragment key={s}>
                <div className="w-full max-w-md text-center py-4 rounded-xl border border-[var(--line)] bg-[var(--surface)] font-serif-display text-lg font-semibold">
                  {s}
                </div>
                {i < p.flow.steps.length - 1 && <ArrowDown size={18} className="text-[var(--gold)]" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PERCEPTION / PRICE ===== */}
      <section className="bg-[var(--black)] text-white py-28 lg:py-36">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 reveal">
          <p className="font-serif-display text-3xl sm:text-5xl font-bold leading-[1.1]">
            {p.perception.line1}
          </p>
          <p className="mt-3 font-serif-display text-3xl sm:text-5xl font-bold leading-[1.1] text-[var(--gold-soft)]">
            {p.perception.line2}
          </p>
          <p className="mt-10 text-lg text-white/70 leading-relaxed max-w-2xl">{p.perception.body}</p>
          <p className="mt-10 font-serif-display text-2xl sm:text-3xl font-semibold">{p.perception.close}</p>
        </div>
      </section>

      {/* ===== AREAS OF EXPERTISE ===== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <p className="eyebrow">{p.areas.eyebrow}</p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-bold leading-[1.06]">
              {p.areas.title}
            </h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)] rounded-2xl overflow-hidden">
            {p.areas.items.map((it, i) => (
              <div key={it.n} className="bg-[var(--surface)] p-8 reveal" style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <span className="font-serif-display text-sm tracking-[0.3em] text-[var(--gold)]">{it.n}</span>
                <h3 className="mt-4 font-serif-display text-2xl font-semibold">{it.title}</h3>
                <p className="mt-3 text-sm text-[var(--muted)] leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BIG STATEMENT ===== */}
      <section className="bg-[var(--ink-2)] border-y border-[var(--line)] py-28 lg:py-40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 reveal">
          <p className="font-serif-display text-4xl sm:text-6xl font-bold leading-[1.05] tracking-[-0.02em]">
            {p.statement[0]}
          </p>
          <p className="mt-5 font-serif-display text-4xl sm:text-6xl font-bold leading-[1.05] tracking-[-0.02em] text-[var(--ivory)]/45">
            {p.statement[1]}
          </p>
        </div>
      </section>

      {/* ===== TRANSFORMATION ===== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <p className="eyebrow">{p.transformation.eyebrow}</p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-bold leading-[1.06]">
              <span className="block text-[var(--ivory)]/45">{p.transformation.title[0]}</span>
              <span className="block">{p.transformation.title[1]}</span>
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-[1fr_auto_1fr] gap-8 items-center">
            <div className="reveal space-y-3">
              {p.transformation.from.map((f) => (
                <div key={f} className="px-5 py-4 rounded-xl bg-[var(--ink-2)] border border-[var(--line)] text-[var(--muted)]">
                  {f}
                </div>
              ))}
            </div>
            <ArrowRight size={28} className="text-[var(--gold)] mx-auto rotate-90 md:rotate-0 reveal" />
            <div className="reveal space-y-3">
              {p.transformation.to.map((t) => (
                <div key={t} className="px-5 py-4 rounded-xl bg-[var(--surface)] border border-[var(--gold)]/30 font-semibold">
                  {t}
                </div>
              ))}
            </div>
          </div>
          <p className="mt-12 font-serif-display text-2xl sm:text-3xl font-semibold text-center reveal">
            {p.transformation.close}
          </p>
        </div>
      </section>

      {/* ===== VPV CONNECTION ===== */}
      <section className="bg-[var(--ink-2)] border-y border-[var(--line)] py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center reveal">
          <p className="eyebrow">{p.vpvLink.eyebrow}</p>
          <h2 className="mt-5 font-serif-display text-3xl sm:text-5xl font-bold">{p.vpvLink.title}</h2>
          <p className="mt-6 text-lg text-[var(--muted)] leading-relaxed max-w-2xl mx-auto">{p.vpvLink.body}</p>
          <Link to="/vpv" className="btn-gold inline-flex mt-9 px-7 py-3.5 rounded-full items-center gap-2 group">
            {p.vpvLink.cta}
            <ArrowUpRight size={17} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </section>

      {/* ===== EXPERIENCE ===== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="rounded-3xl border border-[var(--line)] bg-[var(--ink-2)] overflow-hidden flex items-end justify-center pt-8">
              <img src={p.experience.photo} alt="Nitin Gupta" className="w-full max-w-sm object-contain" />
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow">{p.experience.eyebrow}</p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-bold leading-[1.06]">
              {p.experience.title}
            </h2>
            <p className="mt-7 text-lg text-[var(--muted)] leading-relaxed">{p.experience.story}</p>
            <div className="mt-8">
              <div className="font-serif-display text-2xl font-semibold">{p.experience.name}</div>
              <div className="text-[var(--gold)] mt-1">{p.experience.role}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="bg-[var(--black)] text-white py-28 lg:py-36">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center reveal">
          <h2 className="font-serif-display text-4xl sm:text-6xl font-bold leading-[1.05]">
            {p.finalCta.title}
          </h2>
          <p className="mt-6 text-lg text-white/70">{p.finalCta.body}</p>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex mt-10 px-8 py-4 rounded-full items-center gap-2 group">
            {p.finalCta.cta}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>
    </main>
  );
}
